export function hasTime(dateStr: string): boolean {
  return dateStr.length > 10 && dateStr[10] === 'T'
}

export function parseDateOnly(dateStr: string): Date {
  return new Date(dateStr.slice(0, 10) + 'T12:00:00')
}

// Calendar days from a's date to b's. Counted in UTC so a DST change between
// them (a party on the night clocks fall back) can't stretch one day to 25h.
function calendarDaysBetween(a: string, b: string): number {
  const utcDay = (s: string) => Date.UTC(+s.slice(0, 4), +s.slice(5, 7) - 1, +s.slice(8, 10))
  return Math.round((utcDay(b) - utcDay(a)) / 86_400_000)
}

export function toBcp47(locale: string): string {
  return locale.replace(/_/g, '-')
}

const dayBeforeMonthCache = new Map<string, boolean>()
function isDayBeforeMonth(locale: string): boolean {
  const cached = dayBeforeMonthCache.get(locale)
  if (cached !== undefined) return cached
  const parts = new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
  }).formatToParts(new Date(2000, 0, 15))
  const dayIdx = parts.findIndex(p => p.type === 'day')
  const monthIdx = parts.findIndex(p => p.type === 'month')
  const result = dayIdx >= 0 && monthIdx >= 0 && dayIdx < monthIdx
  dayBeforeMonthCache.set(locale, result)
  return result
}

function formatTime(dateStr: string, locale: string): string {
  const match = dateStr.match(/T(\d{2}:\d{2})/)
  return match ? formatClockTime(match[1], locale) : ''
}

/** A wall-clock `HH:mm` in the locale's style ("8:30 PM", "20:30"), minutes dropped on the hour. */
export function formatClockTime(hhmm: string, locale: string): string {
  const [h, m] = hhmm.split(':').map(n => parseInt(n, 10))
  if (Number.isNaN(h) || Number.isNaN(m)) return ''
  return new Date(2000, 0, 1, h, m).toLocaleTimeString(toBcp47(locale), {
    hour: 'numeric',
    minute: m > 0 ? '2-digit' : undefined,
  })
}

/**
 * Just the hours of a one-day event — one that starts and ends on a day, or runs
 * overnight — e.g. "9:30 PM – 2 AM", or '' when it has no time. Null when the
 * event spans days, so a caller can fall back to its date range.
 */
export function formatEventTimeRange(startDate: string, endDate: string | undefined | null, locale: string): string | null {
  const sameDay = !endDate || startDate.slice(0, 10) === endDate.slice(0, 10)
  const overnight = !!endDate && hasTime(startDate) && hasTime(endDate)
    && calendarDaysBetween(startDate, endDate) <= 1
  if (!sameDay && !overnight) return null
  if (!hasTime(startDate)) return ''
  const start = formatTime(startDate, locale)
  return endDate && hasTime(endDate) ? `${start} – ${formatTime(endDate, locale)}` : start
}

export function formatEventDateRange(
  startDate: string,
  endDate: string | undefined | null,
  locale: string,
  now: Date,
): string {
  const tag = toBcp47(locale)
  const start = parseDateOnly(startDate)
  const startDay = startDate.slice(0, 10)

  if (!endDate) {
    const datePart = start.toLocaleDateString(tag, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: start.getFullYear() === now.getFullYear() ? undefined : 'numeric',
    })
    if (hasTime(startDate)) {
      return `${datePart}, ${formatTime(startDate, locale)}`
    }
    return datePart
  }

  const end = parseDateOnly(endDate)
  const endDay = endDate.slice(0, 10)
  const sameDay = startDay === endDay
  const sameMonth = start.getFullYear() === end.getFullYear() && start.getMonth() === end.getMonth()
  const sameYear = start.getFullYear() === end.getFullYear()
  const sameYearAsNow = start.getFullYear() === now.getFullYear()

  const isOvernight =
    !sameDay &&
    hasTime(startDate) &&
    hasTime(endDate) &&
    calendarDaysBetween(startDate, endDate) <= 1

  if (sameDay || isOvernight) {
    const datePart = start.toLocaleDateString(tag, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: sameYearAsNow ? undefined : 'numeric',
    })
    if (hasTime(startDate)) {
      const startTime = formatTime(startDate, locale)
      const endTime = hasTime(endDate) ? formatTime(endDate, locale) : null
      return endTime ? `${datePart}, ${startTime} – ${endTime}` : `${datePart}, ${startTime}`
    }
    return datePart
  }

  if (sameMonth) {
    const yearSuffix = sameYearAsNow ? '' : `, ${end.getFullYear()}`
    if (isDayBeforeMonth(tag)) {
      const startDay = start.toLocaleDateString(tag, { day: 'numeric' })
      const endMonthDay = end.toLocaleDateString(tag, { month: 'short', day: 'numeric' })
      return `${startDay} – ${endMonthDay}${yearSuffix}`
    }
    const startMonthDay = start.toLocaleDateString(tag, { month: 'short', day: 'numeric' })
    const endDay = end.toLocaleDateString(tag, { day: 'numeric' })
    return `${startMonthDay} – ${endDay}${yearSuffix}`
  }

  if (sameYear) {
    const startStr = start.toLocaleDateString(tag, { month: 'short', day: 'numeric' })
    const endStr = end.toLocaleDateString(tag, { month: 'short', day: 'numeric' })
    return sameYearAsNow
      ? `${startStr} – ${endStr}`
      : `${startStr} – ${endStr}, ${end.getFullYear()}`
  }

  const startStr = start.toLocaleDateString(tag, { month: 'short', day: 'numeric' })
  const endStr = end.toLocaleDateString(tag, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
  return `${startStr} – ${endStr}`
}

/**
 * The dates a festival actually runs: its official startDate/endDate, widened
 * to cover any set day outside them (a pre-party the day before, a closing
 * night past the listed end). Everything that asks "is this festival live?"
 * goes through here so the event page, event cards and festival mode agree.
 *
 * `setDays` wins over the stored `firstSetDay`/`lastSetDay` when a caller has
 * the sets loaded — it's fresher than the trigger-maintained copy.
 */
export function festivalWindow(
  event: { startDate: string; endDate?: string | null; firstSetDay?: string; lastSetDay?: string },
  setDays?: readonly string[],
): { startDate: string; endDate?: string } {
  const first = setDays?.length ? setDays.reduce((a, b) => (b < a ? b : a)) : event.firstSetDay
  const last = setDays?.length ? setDays.reduce((a, b) => (b > a ? b : a)) : event.lastSetDay
  const officialEnd = event.endDate ?? event.startDate
  return {
    startDate: first && first < event.startDate.slice(0, 10) ? first : event.startDate,
    endDate: last && last > officialEnd.slice(0, 10) ? last : (event.endDate ?? undefined),
  }
}
